/**
 *
 * flatInvoiceDetails
 *
 */

import {
  DetectInvoiceResponse,
  Detection,
  DetectedContact,
  DetectedSupplier,
} from './detectInvoice';
import {
  InvoicePredictionDetailType,
  InvoiceDetailType,
  InvoiceDetailContactType,
  InvoiceDetailContactAddressType,
  InvoiceAdditionalPropertiesType,
  InvoiceDetailSupplierType,
  InvoiceDetailClientType,
} from './invoiceDetails';

export type FlatInvoiceDetailInputType =
  | 'number'
  | 'date'
  | 'invoice-or-creditmemo'
  | 'text'
  | 'autocomplete';

export function getInvoiceDetailTypeFieldType(
  invoicePredictionType: string,
): FlatInvoiceDetailInputType {
  // switch (invoicePredictionType) {
  //   case InvoicePredictionDetailType.GrandTotalAmount:
  //   case InvoicePredictionDetailType.VatRate:
  //   case InvoicePredictionDetailType.NetTotalAmount:
  //   case InvoicePredictionDetailType.VatAmount:
  //   case InvoicePredictionDetailType.NetAmount:
  //   case InvoicePredictionDetailType.VatTotalAmount:
  //     return 'number';
  //   case InvoicePredictionDetailType.DeliveryDate:
  //   case InvoicePredictionDetailType.InvoiceDate:
  //   case InvoicePredictionDetailType.SenderOrderDate:
  //   case InvoicePredictionDetailType.ReceiverOrderDate:
  //     return 'date';
  //   case InvoicePredictionDetailType.DocumentType:
  //     return 'invoice-or-creditmemo';
  //   default:
  //     return 'text';
  // }
  switch (invoicePredictionType) {
    case 'GrandTotalAmount':
    case 'VatRate':
    case 'NetTotalAmount':
    case 'VatAmount':
    case 'NetAmount':
    case 'VatTotalAmount':
    case 'DiscountDuration':
    case 'DiscountPercent':
    case 'DueDateDuration':
      return 'number';
    case 'DeliveryDate':
    case 'InvoiceDate':
    case 'SenderOrderDate':
    case 'ReceiverOrderDate':
    case 'DueDateDate':
    case 'DiscountDate':
      return 'date';
    case 'DocumentType':
      return 'invoice-or-creditmemo';
    default:
      return 'text';
  }
}

export interface FlatInvoiceDetail {
  Id: string;
  Type:
    | InvoicePredictionDetailType
    | InvoiceDetailType
    | InvoiceDetailContactType
    | string
    | InvoiceAdditionalPropertiesType
    | InvoiceDetailSupplierType
    | InvoiceDetailClientType
    | InvoiceDetailContactAddressType;
  X: number;
  Y: number;
  Width: number;
  Height: number;
  Path: string; // path to value starting from the `Invoice`
  InputType: FlatInvoiceDetailInputType;
  Disabled?: boolean;
}

export type FlatInvoiceDetails = FlatInvoiceDetail[];

export interface FlatInvoiceDetailsGroup {
  Id: string;
  Type:
    | InvoicePredictionDetailType
    | InvoiceDetailType
    | InvoiceDetailContactType
    | InvoiceAdditionalPropertiesType
    | InvoiceDetailSupplierType
    | InvoiceDetailClientType
    | InvoiceDetailContactAddressType;
  InvoiceDetailsMap: FlatInvoiceDetails;
}
export type FlatInvoiceDetailsGroups = FlatInvoiceDetailsGroup[];

export function flattenDetectInvoiceResponse(
  response: DetectInvoiceResponse,
): {
  invoiceDetailsMap: FlatInvoiceDetails;
  invoiceDetailsGroupsMap: FlatInvoiceDetailsGroups;
} {
  function mapInvoiceDetailTypePredictions(
    predictions: Detection[],
    groupId: string | null,
    groupIndex: number | null,
  ): FlatInvoiceDetails {
    return predictions.map(({ TypeName, X, Y, Width, Height }, index) => ({
      Id: groupId ? `${groupId}-${TypeName === undefined ? 'LineItem' : TypeName}-${index}` : `${TypeName}-${index}`,
      Type: TypeName,
      X,
      Y,
      Width,
      Height,
      Path:
        typeof groupIndex === 'number'
          ? `Invoice.DetectInvoiceResponse.PredictionGroups[${groupIndex}].InvoiceDetailTypePredictions[${index}].Value`
          : `Invoice.DetectInvoiceResponse.InvoiceDetailTypePredictions[${index}].Value`,
      InputType: getInvoiceDetailTypeFieldType(TypeName),
    }));
  }

  function mapInvoiceDetailContactType(
    detail: DetectedContact | Record<string, string> | DetectedSupplier,
    detailType: InvoiceDetailType,
  ): FlatInvoiceDetails {
    if ((detail as any).Score) {
      return Object.values(InvoiceDetailContactType).flatMap((contactKey) => {
        // address
        if (contactKey === InvoiceDetailContactType.Address) {
          const { Country, City, ZipCode, Street } = (detail as any)[contactKey];
          return [Country, City, ZipCode, Street].map(({ X, Y, Width, Height, Name }) => ({
            Id: `${detailType}-Address-${Name}`,
            Type: Name,
            X,
            Y,
            Width,
            Height,
            Path: `Invoice.DetectInvoiceResponse.${detailType}.Address.${Name}.Value`,
            InputType: 'text' as 'text',
          }));
        }

        if (
          contactKey === InvoiceDetailContactType.Email ||
          contactKey === InvoiceDetailContactType.WebsiteUrl
        ) {
          // arrays
          return (detail as any)[contactKey].map(
            (
              { Name, X, Y, Width, Height }: { Name: any; X: any; Y: any; Width: any; Height: any },
              index: any,
            ) => ({
              Id: `${detailType}-${Name}-${index}`,
              Type: Name,
              X,
              Y,
              Width,
              Height,
              Path: `Invoice.DetectInvoiceResponse.${detailType}.${contactKey}[${index}].Value`,
              InputType: 'text' as 'text',
            }),
          );
        }

        // objects
        const { Name, X, Y, Width, Height } = (detail as any)[contactKey];
        return {
          Id: `${detailType}-${Name}`,
          Type: Name,
          X,
          Y,
          Width,
          Height,
          Path: `Invoice.DetectInvoiceResponse.${detailType}.${contactKey}.Value`,
          InputType: 'text' as 'text',
        };
      });
    } else if (detailType === InvoiceDetailType.AdditionalProperties) {
      return Object.values(InvoiceAdditionalPropertiesType).flatMap((contactKey) => {
        return {
          Id: `${detailType}-${contactKey}`,
          Type: contactKey,
          X: 0,
          Y: 0,
          Width: 0,
          Height: 0,
          Path: `Invoice.DetectInvoiceResponse.${detailType}.${contactKey}`,
          InputType: 'text' as 'text',
          Disabled: true,
        };
      });
    } else if (detailType === InvoiceDetailType.Supplier) {
      return Object.values(InvoiceDetailSupplierType).flatMap((key) => {
        if (key === InvoiceDetailSupplierType.Ibans || key === InvoiceDetailSupplierType.Uids) {
          return (detail as any)[key].map((_item: any, index: any) => ({
            Id: `${detailType}-${key}-${index}`,
            Type: key,
            X: 0,
            Y: 0,
            Width: 0,
            Height: 0,
            Path: `Invoice.DetectInvoiceResponse.${detailType}.${key}[${index}]`,
            InputType: 'autocomplete' as 'autocomplete',
          }));
        } else
          return {
            Id: `${detailType}-${key}`,
            Type: key,
            X: 0,
            Y: 0,
            Width: 0,
            Height: 0,
            Path: `Invoice.DetectInvoiceResponse.${detailType}.${key}`,
            InputType: 'autocomplete' as 'autocomplete',
          };
      });
    } else {
      return Object.values(InvoiceDetailClientType).flatMap((key) => {
        return {
          Id: `${detailType}-${key}`,
          Type: key,
          X: 0,
          Y: 0,
          Width: 0,
          Height: 0,
          Path: `Invoice.DetectInvoiceResponse.${detailType}.${key}`,
          InputType: 'autocomplete' as 'autocomplete',
        };
      });
    }
  }

  const invoiceDetailsMap = mapInvoiceDetailTypePredictions(
    response.InvoiceDetailTypePredictions,
    null,
    null,
    // the filter happens after the map to preserve correct index locations
  ).filter(({ Type }) => Type !== InvoicePredictionDetailType.Sender);

  const lineItemsDetails = [
    'ItemId',
    'PositionNumber',
    'Description',
    'Quantity',
    'UnitPrice',
    'TotalAmount',
    'OrderId',
    'DeliveryId',
    'Unit',
    'VatRate',
    'DeliveryDate',
    'Discount',
    'PositionId',
  ];

  const invoiceDetailsGroupsMap: FlatInvoiceDetailsGroup[] = [
    ...response.PredictionGroups.map(({ TypeName, InvoiceDetailTypePredictions }, index) => {
    return ({
      Id: `${TypeName}-${index}`,
      Type: TypeName,
      InvoiceDetailsMap: mapInvoiceDetailTypePredictions(
        InvoiceDetailTypePredictions,
        `${TypeName}-${index}`,
        index,
      ),
    })}),

    ...response.LineItemTable.LineItems.map((lineItem, index) => {
    return ({
      Id: `LineItemGroup-${index}`,
      Type: 'LineItemGroup',
      InvoiceDetailsMap: lineItemsDetails.map((lineItemDetail, j) => {
        return {
          Id: `LineItemGroup-${index}-LineItem${lineItemDetail}-${j}`,
          Type: `Type${lineItemDetail}`,
          X: lineItem[lineItemDetail].X,
          Y: lineItem[lineItemDetail].Y,
          Width: lineItem[lineItemDetail].Width,
          Height: lineItem[lineItemDetail].Height,
          Path: `Invoice.DetectInvoiceResponse.LineItemTable.LineItems[${index}].InvoiceDetailTypePredictions[${index}].Value`,
          InputType: undefined,
        }
      }),
    })}),

    ...Object.values(InvoiceDetailType)
      .filter((detailType) => !!response[detailType])
      .flatMap((detailType) => {
        const detail = response[detailType]!; // undefined elements are skipped by filter method
        return {
          Id: detailType,
          Type: detailType,
          InvoiceDetailsMap: mapInvoiceDetailContactType(detail as any, detailType),
        };
      }),
  ];

  return { invoiceDetailsMap, invoiceDetailsGroupsMap };
}
